/**
 * Week/Month Selector Component
 * Navigation controls for selecting week or month date ranges
 */

import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  formatWeekRange,
  formatMonth,
  prevWeek,
  nextWeek,
  prevMonth,
  nextMonth,
  getCurrentWeekStart,
  getCurrentMonthStart,
  isSameDate,
  startOfMonth,
} from '@/lib/utils/dateHelpers';

type NavigationMode = 'week' | 'month';

interface WeekSelectorProps {
  weekStart: Date;
  onWeekChange: (newStart: Date) => void;
  disabled?: boolean;
  /** Navigation mode: 'week' navigates by week, 'month' navigates by month */
  navigationMode?: NavigationMode;
}

/**
 * WeekSelector - Navigate between weeks or months with prev/next buttons
 *
 * @example
 * // Week navigation (default)
 * <WeekSelector
 *   weekStart={currentWeek}
 *   onWeekChange={setCurrentWeek}
 * />
 *
 * @example
 * // Month navigation
 * <WeekSelector
 *   weekStart={currentMonth}
 *   onWeekChange={setCurrentMonth}
 *   navigationMode="month"
 * />
 */
export function WeekSelector({
  weekStart,
  onWeekChange,
  disabled = false,
  navigationMode = 'week',
}: WeekSelectorProps) {
  const isMonthMode = navigationMode === 'month';

  const handlePrev = () => {
    onWeekChange(isMonthMode ? prevMonth(weekStart) : prevWeek(weekStart));
  };

  const handleNext = () => {
    onWeekChange(isMonthMode ? nextMonth(weekStart) : nextWeek(weekStart));
  };

  const handleToday = () => {
    onWeekChange(isMonthMode ? getCurrentMonthStart() : getCurrentWeekStart());
  };

  // Determine if we're viewing the current period
  const currentPeriodStart = isMonthMode ? getCurrentMonthStart() : getCurrentWeekStart();
  const isCurrentPeriod = isMonthMode
    ? startOfMonth(weekStart).getTime() === currentPeriodStart.getTime()
    : isSameDate(weekStart, currentPeriodStart);

  // Format the label based on mode
  const label = isMonthMode ? formatMonth(weekStart) : formatWeekRange(weekStart);

  return (
    <div className="flex items-center justify-between gap-4 py-4">
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={handlePrev}
        disabled={disabled}
        aria-label={isMonthMode ? 'Previous month' : 'Previous week'}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <div className="flex items-center gap-3">
        <CalendarDays className="h-5 w-5 text-muted-foreground" />
        <span className="text-lg font-medium">
          {label}
        </span>
        {!isCurrentPeriod && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleToday}
            disabled={disabled}
            className="text-muted-foreground hover:text-foreground"
          >
            Today
          </Button>
        )}
      </div>

      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={handleNext}
        disabled={disabled}
        aria-label={isMonthMode ? 'Next month' : 'Next week'}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
